<template>
  <div
    class="table-wrapper"
    :class="[
      { fullscreen: isFullscreen },
      { 'global-ai-content': isFullscreen },
    ]"
    :style="wrapperStyle"
  >
    <div class="table-header">
      <div class="table-header-title">表格</div>
      <div class="table-header-actions">
        <div class="table-header-actions__btn" @click="copyHtml">
          <img :src="cdnUrl + 'ai-table-copy.svg'" />
        </div>
        <div class="table-header-actions__btn" @click="downloadExcel">
          <img :src="cdnUrl + 'ai-table-download.svg'" />
        </div>
        <div class="table-header-actions__btn" @click="toggleFullscreen">
          <img
            :src="
              cdnUrl +
              (isFullscreen ? 'ai-table-close.svg' : 'ai-table-full.svg')
            "
          />
        </div>
      </div>
    </div>
    <div class="table-body">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "mdTable",
  props: {
    html: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      cdnUrl: "https://cdn-image.yucrm.cn/images/tb-front-platform/images/ai/",
      isFullscreen: false,
      originalParent: null,
    };
  },
  computed: {
    wrapperStyle() {
      if (this.isFullscreen) {
        return {
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100vh",
          zIndex: "999",
          backgroundColor: "#fff",
        };
      }
      return {};
    },
  },
  methods: {
    copyHtml() {
      const table = this.$el.querySelector("table");
      if (!table) return;

      // 清除选中状态
      window.getSelection().removeAllRanges();

      // 构建带边框样式的HTML表格（无背景色）
      const clone = table.cloneNode(true);

      // 移除背景色
      const style = document.createElement("style");
      style.textContent = `
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #e5e9f2; padding: 8px 12px; text-align: left; }
        th { font-weight: 600; }
      `;

      const wrapper = document.createElement("div");
      wrapper.appendChild(style);
      wrapper.appendChild(clone);

      const htmlContent = wrapper.innerHTML;

      // 同时构建纯文本格式（用于粘贴到纯文本编辑器）
      const textRows = [];
      const trs = table.querySelectorAll("tr");
      trs.forEach((tr) => {
        const cells = [];
        const tds = tr.querySelectorAll("td, th");
        tds.forEach((td) => {
          cells.push(td.textContent.trim());
        });
        textRows.push(cells.join("\t"));
      });
      const textContent = textRows.join("\n");

      // 使用 Clipboard API 同时复制HTML和纯文本格式
      const htmlBlob = new Blob([htmlContent], { type: "text/html" });
      const textBlob = new Blob([textContent], { type: "text/plain" });
      const clipboardItem = new ClipboardItem({
        "text/html": htmlBlob,
        "text/plain": textBlob,
      });

      navigator.clipboard
        .write([clipboardItem])
        .then(() => {
          this.$message.success("复制成功");
        })
        .catch(() => {
          this.$message.error("复制失败");
        });
    },
    downloadExcel() {
      const table = this.$el.querySelector("table");
      if (!table) return;

      // 将表格转换为CSV格式
      const rows = [];
      const trs = table.querySelectorAll("tr");
      trs.forEach((tr) => {
        const cells = [];
        const tds = tr.querySelectorAll("td, th");
        tds.forEach((td) => {
          // 处理包含逗号、引号或换行符的内容
          let text = td.textContent.trim();
          if (text.includes(",") || text.includes('"') || text.includes("\n")) {
            text = '"' + text.replace(/"/g, '""') + '"';
          }
          cells.push(text);
        });
        rows.push(cells.join(","));
      });

      // 添加BOM头以支持中文
      const bom = "\uFEFF";
      const csvContent = bom + rows.join("\n");

      // 生成带日期时间的文件名
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      const seconds = String(now.getSeconds()).padStart(2, "0");
      const fileName = `表格_${year}${month}${day} ${hours}-${minutes}-${seconds}.csv`;

      // 创建CSV文件下载
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    },
    toggleFullscreen() {
      if (this.isFullscreen) {
        this.exitFullscreen();
      } else {
        this.enterFullscreen();
      }
    },
    enterFullscreen() {
      this.isFullscreen = true;
      document.body.style.overflow = "hidden";
      const el = this.$el;
      this.originalParent = el.parentElement;
      document.body.appendChild(el);
    },
    exitFullscreen() {
      this.isFullscreen = false;
      document.body.style.overflow = "";
      const el = this.$el;
      if (this.originalParent) {
        this.originalParent.appendChild(el);
        this.originalParent = null;
      }
    },
  },
  beforeDestroy() {
    if (this.isFullscreen) {
      this.exitFullscreen();
    }
  },
};
</script>

<style lang="scss" scoped>
.table-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  max-width: 100%;
  overflow: hidden;
  width: fit-content;
}
.table-header {
  align-items: center;
  background-color: #f3f5f7;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  justify-content: space-between;
  min-height: 30px;
  padding: 7px 12px;
  position: relative;
  border: 1px solid #e5e9f2;
  border-bottom: none;
  &-title {
    font-weight: bold;
    font-size: 12px;
    color: #333333;
  }
  &-actions {
    display: flex;
    align-items: center;
    &__btn {
      width: 16px;
      height: 16px;
      cursor: pointer;
      img {
        width: 100%;
        height: 100%;
      }
      & + & {
        margin-left: 16px;
      }
    }
  }
}
.table-body {
  overflow: auto;
  scrollbar-color: #e3e3e3 transparent;
}

.fullscreen {
  display: flex;
  flex-direction: column;
}

.fullscreen .table-header {
  border-radius: 8px 8px 0 0;
  border-bottom: 1px solid #e5e9f2;
}

.fullscreen .table-body {
  height: calc(100% - 44px);
  flex: 1;
  margin: 0 auto;
  display: grid;
  align-items: center;
}
</style>